import React, { useState, useEffect } from 'react';
import { useContratos } from '../../hooks/useContratos';
import { ConfirmModal } from '../common/ConfirmModal';
import { MenuAcciones } from '../common/MenuAcciones';
import { formatCurrencyInt } from '../../utils/formatters';
import { nombreMes, periodoDesde, sumarMeses, fechaCorta, diasHasta } from '../../utils/periodos';

// Proveedores en orden fijo; el color sigue al proveedor y siempre va con su nombre.
const PROVEEDORES = [
  { id: 'tbbc', label: 'TBBC' },
  { id: 'optimize', label: 'Optimize' },
  { id: 'axd', label: 'AXD' }
];
const labelDe = (id) => PROVEEDORES.find((p) => p.id === id)?.label || id;

const fechaISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Desplaza una fecha 'AAAA-MM-DD' en años y días.
const moverFecha = (fecha, anios, dias) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha || '')) return '';
  const [y, m, d] = fecha.split('-').map(Number);
  return fechaISO(new Date(y + anios, m - 1, d + dias));
};

const Cerrar = ({ onClick, disabled }) => (
  <button className="modal-close" onClick={onClick} aria-label="Cerrar" disabled={disabled}>
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  </button>
);

/**
 * Crear, renovar o editar un contrato. Al crear/renovar se listan las
 * herramientas del catálogo del proveedor con su tarifa pactada (precargada
 * con la del contrato anterior o, si no hay, con el valor del catálogo).
 */
const ContratoModal = ({ modo, proveedor, contrato, herramientas, onClose, onSubmit }) => {
  const editar = modo === 'editar';
  const inicioSugerido = modo === 'renovar'
    ? moverFecha(contrato.fecha_fin, 0, 1)
    : fechaISO(new Date());
  const [referencia, setReferencia] = useState(editar ? contrato.referencia || '' : '');
  const [inicio, setInicio] = useState(editar ? contrato.fecha_inicio : inicioSugerido);
  // Un año de contrato por defecto: fin = inicio + 1 año - 1 día.
  const [fin, setFin] = useState(editar ? contrato.fecha_fin : moverFecha(inicioSugerido, 1, -1));
  const [notas, setNotas] = useState(editar ? contrato.notas || '' : '');
  const [tarifas, setTarifas] = useState(() =>
    (herramientas || []).map((h) => ({ ...h, incluir: true, costo: String(h.costo ?? '') }))
  );
  const [enviando, setEnviando] = useState(false);

  const fechasOk = /^\d{4}-\d{2}-\d{2}$/.test(inicio) && /^\d{4}-\d{2}-\d{2}$/.test(fin) && fin >= inicio;
  const tarifasOk = editar || tarifas.every((t) => !t.incluir || parseFloat(t.costo) >= 0);
  const cambiarTarifa = (i, cambios) => setTarifas((l) => l.map((x, k) => (k === i ? { ...x, ...cambios } : x)));

  const enviar = async () => {
    setEnviando(true);
    const ok = await onSubmit({
      referencia,
      fecha_inicio: inicio,
      fecha_fin: fin,
      notas,
      tarifas: editar ? undefined : tarifas
        .filter((t) => t.incluir)
        .map((t) => ({ app_id: t.app_id, name: t.name, costo: parseFloat(t.costo) }))
    });
    setEnviando(false);
    if (ok) onClose();
  };

  const titulo = { nuevo: 'Registrar contrato', renovar: 'Nuevo contrato (renovación)', editar: 'Editar contrato' }[modo];

  return (
    <div className="modal-overlay" onClick={enviando ? undefined : onClose}>
      <div className="modal-content contrato-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h3 className="modal-title">{titulo} · {labelDe(proveedor)}</h3>
          </div>
          <Cerrar onClick={onClose} disabled={enviando} />
        </div>
        <div className="modal-body consumo-form">
          <label className="consumo-campo">
            <span>Referencia del contrato · opcional</span>
            <input
              className="proceso-input"
              value={referencia}
              maxLength={120}
              placeholder="Ej. Contrato 2026-0145"
              onChange={(e) => setReferencia(e.target.value)}
            />
          </label>
          <div className="contrato-fechas">
            <label className="consumo-campo">
              <span>Inicio</span>
              <input
                className="proceso-input"
                type="date"
                value={inicio}
                onChange={(e) => {
                  setInicio(e.target.value);
                  if (!editar) setFin(moverFecha(e.target.value, 1, -1));
                }}
              />
            </label>
            <label className="consumo-campo">
              <span>Fin</span>
              <input className="proceso-input" type="date" value={fin} onChange={(e) => setFin(e.target.value)} />
            </label>
          </div>
          {!fechasOk && <small className="contrato-error">La fecha de fin no puede ser anterior al inicio</small>}
          <label className="consumo-campo">
            <span>Notas · opcional</span>
            <textarea
              className="proceso-input contrato-notas"
              rows={2}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
            />
          </label>

          {!editar && (
            <div className="consumo-campo">
              <span>Tarifa pactada por herramienta (COP / mes)</span>
              {tarifas.length === 0 ? (
                <p className="consumo-ayuda">
                  Este proveedor no tiene herramientas en su catálogo. Puedes agregarlas después.
                </p>
              ) : (
                <div className="contrato-tarifas">
                  {tarifas.map((t, i) => (
                    <label key={t.app_id} className={`contrato-tarifa ${t.incluir ? '' : 'excluida'}`}>
                      <input
                        type="checkbox"
                        checked={t.incluir}
                        onChange={(e) => cambiarTarifa(i, { incluir: e.target.checked })}
                      />
                      <span className="contrato-tarifa-nombre">{t.name}</span>
                      <span className="contrato-tarifa-pesos">$</span>
                      <input
                        className="contrato-tarifa-input"
                        type="number"
                        min="0"
                        step="1"
                        value={t.costo}
                        disabled={!t.incluir}
                        onChange={(e) => cambiarTarifa(i, { costo: e.target.value })}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="modal-btn modal-btn-ghost" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button className="modal-btn modal-btn-primary" disabled={!fechasOk || !tarifasOk || enviando} onClick={enviar}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Ajustar la tarifa de una herramienta (o agregarla al contrato). */
const TarifaModal = ({ herramienta, contrato, periodo, agregar, onClose, onSubmit }) => {
  const minMes = contrato.fecha_inicio.slice(0, 7);
  const maxMes = contrato.fecha_fin.slice(0, 7);
  const sugerido = agregar ? minMes : (periodo < minMes ? minMes : periodo > maxMes ? maxMes : periodo);
  const [valor, setValor] = useState(agregar ? String(herramienta.costo ?? '') : '');
  const [desde, setDesde] = useState(sugerido);
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const valido = parseFloat(valor) >= 0 && desde >= minMes && desde <= maxMes && (agregar || motivo.trim());

  const enviar = async () => {
    setEnviando(true);
    const r = await onSubmit({ app_id: herramienta.app_id, name: herramienta.name, costo: parseFloat(valor), desde, motivo });
    setEnviando(false);
    if (r) onClose();
  };

  return (
    <div className="modal-overlay" onClick={enviando ? undefined : onClose}>
      <div className="modal-content consumo-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-header-text">
            <h3 className="modal-title">{agregar ? 'Agregar al contrato' : 'Ajustar tarifa'} · {herramienta.name}</h3>
          </div>
          <Cerrar onClick={onClose} disabled={enviando} />
        </div>
        <div className="modal-body consumo-form">
          {!agregar && (
            <p className="consumo-ayuda">
              Tarifa vigente: <strong>{formatCurrencyInt(herramienta.costo)}</strong>. El ajuste es la
              excepción: la tarifa anterior queda en el historial y sigue aplicando a los meses previos.
            </p>
          )}
          <label className="consumo-campo">
            <span>{agregar ? 'Tarifa pactada (COP / mes)' : 'Nueva tarifa (COP / mes)'}</span>
            <input className="proceso-input" type="number" min="0" step="1" value={valor} onChange={(e) => setValor(e.target.value)} />
          </label>
          <label className="consumo-campo">
            <span>Rige desde</span>
            <input
              className="proceso-input"
              type="month"
              min={minMes}
              max={maxMes}
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
            <small className="consumo-ayuda">
              Dentro del contrato: {fechaCorta(contrato.fecha_inicio)} → {fechaCorta(contrato.fecha_fin)}
            </small>
          </label>
          {!agregar && (
            <label className="consumo-campo">
              <span>Motivo del ajuste</span>
              <input
                className="proceso-input"
                value={motivo}
                placeholder="Ej. Ajuste por IPC, otrosí No. 2"
                onChange={(e) => setMotivo(e.target.value)}
              />
            </label>
          )}
        </div>
        <div className="modal-footer">
          <button className="modal-btn modal-btn-ghost" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button className="modal-btn modal-btn-primary" disabled={!valido || enviando} onClick={enviar}>
            {enviando ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Historial de tarifas de una herramienta en el contrato. */
const HistorialModal = ({ tarifa, contrato, onClose, onDeshacer }) => (
  <div className="modal-overlay" onClick={onClose}>
    <div className="modal-content consumo-modal" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <div className="modal-header-text">
          <h3 className="modal-title">Historial · {tarifa.name}</h3>
        </div>
        <Cerrar onClick={onClose} />
      </div>
      <div className="modal-body">
        <ul className="contrato-historial">
          {[...tarifa.historial].reverse().map((h) => {
            const base = h.vigente_desde === contrato.fecha_inicio;
            return (
              <li key={h.id}>
                <div>
                  <strong>{formatCurrencyInt(h.costo)}</strong>
                  <span className="contrato-historial-desde">desde {fechaCorta(h.vigente_desde)}</span>
                  <div className="consumo-ayuda">
                    {base ? 'Tarifa pactada en el contrato' : `Ajuste: ${h.motivo || '—'}`}
                    {h.creado_por && ` · ${h.creado_por}`}
                  </div>
                </div>
                {!base && (
                  <button type="button" className="contrato-link-btn" onClick={() => onDeshacer(h)}>
                    Deshacer
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div className="modal-footer">
        <button className="modal-btn modal-btn-ghost" onClick={onClose}>Cerrar</button>
      </div>
    </div>
  </div>
);

/** Estado del contrato respecto a hoy. */
const estadoContrato = (c) => {
  const aInicio = diasHasta(c.fecha_inicio);
  const aFin = diasHasta(c.fecha_fin);
  if (aInicio > 0) return { clase: 'futuro', texto: `Inicia en ${aInicio} días` };
  if (aFin < 0) return { clase: 'vencido', texto: 'Vencido' };
  if (aFin <= 60) return { clase: 'por-vencer', texto: aFin === 0 ? 'Vence hoy' : `Vence en ${aFin} días` };
  return { clase: 'vigente', texto: `Vigente · vence en ${aFin} días` };
};

/**
 * Módulo Costos Proveedor (2026-10). Lo que cuesta cada herramienta según el
 * contrato con su proveedor (normalmente anual), en una tabla por proveedor.
 * Se consulta por mes: muestra el contrato que cubre ese mes y la tarifa que
 * regía (un ajuste de tarifa es la excepción y queda en el historial). Sin
 * sumas: lo que se paga por proveedor, área o cliente va en la liquidación.
 */
export const CostosProveedorModule = ({ showNotification }) => {
  const {
    datos, loading, loadVigentes,
    crearContrato, editarContrato, eliminarContrato,
    guardarTarifa, deshacerAjuste, quitarHerramienta
  } = useContratos(showNotification);

  const [periodo, setPeriodo] = useState(periodoDesde(0));
  const [contratoModal, setContratoModal] = useState(null); // { modo, proveedor, contrato?, herramientas? }
  const [tarifaModal, setTarifaModal] = useState(null); // { contrato, herramienta, agregar }
  const [historial, setHistorial] = useState(null); // { tarifa, contrato }
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (/^\d{4}-\d{2}$/.test(periodo)) loadVigentes(periodo);
  }, [periodo, loadVigentes]);

  const recargar = () => loadVigentes(periodo);
  const ok = (msg) => showNotification?.('success', 'Costos Proveedor', msg);
  const porProveedor = (id) => (datos?.periodo === periodo ? datos.proveedores.find((p) => p.proveedor === id) : null);

  // Herramientas para un contrato nuevo: catálogo activo, con la tarifa del
  // contrato anterior si la había (renovación) o el valor del catálogo.
  const herramientasPara = (p) => p.catalogo.map((h) => {
    const previa = p.tarifas.find((t) => t.app_id === h.id);
    return { app_id: h.id, name: h.name, costo: previa?.costo ?? h.costo };
  });

  const guardarContrato = async (d) => {
    const { modo, proveedor, contrato } = contratoModal;
    const r = modo === 'editar'
      ? await editarContrato(contrato.id, d)
      : await crearContrato({ ...d, proveedor });
    if (r) {
      ok(modo === 'editar' ? 'Contrato actualizado' : `Contrato de ${labelDe(proveedor)} registrado`);
      // Si el contrato no cubre el mes en pantalla, se salta a su primer mes.
      if (d.fecha_inicio.slice(0, 7) > periodo || d.fecha_fin.slice(0, 7) < periodo) {
        setPeriodo(d.fecha_inicio.slice(0, 7));
      } else {
        recargar();
      }
    }
    return r;
  };

  const accionesContrato = (p) => [
    {
      label: 'Editar contrato',
      onClick: () => setContratoModal({ modo: 'editar', proveedor: p.proveedor, contrato: p.contrato })
    },
    {
      label: 'Nuevo contrato (renovación)',
      onClick: () => setContratoModal({
        modo: 'renovar', proveedor: p.proveedor, contrato: p.contrato, herramientas: herramientasPara(p)
      })
    },
    {
      label: 'Eliminar contrato',
      danger: true,
      onClick: () => setConfirm({
        title: 'Eliminar contrato',
        message: `¿Eliminar el contrato de ${labelDe(p.proveedor)} (${fechaCorta(p.contrato.fecha_inicio)} → ${fechaCorta(p.contrato.fecha_fin)})? Se borran sus tarifas y ajustes.`,
        onConfirm: async () => {
          setConfirm(null);
          if (await eliminarContrato(p.contrato.id)) {
            ok('Contrato eliminado');
            recargar();
          }
        }
      })
    }
  ];

  const accionesTarifa = (p, t) => [
    {
      label: 'Ajustar tarifa',
      onClick: () => setTarifaModal({ contrato: p.contrato, herramienta: t, agregar: false })
    },
    { label: 'Ver historial', onClick: () => setHistorial({ tarifa: t, contrato: p.contrato }) },
    {
      label: 'Quitar del contrato',
      danger: true,
      onClick: () => setConfirm({
        title: 'Quitar del contrato',
        message: `¿Quitar "${t.name}" del contrato de ${labelDe(p.proveedor)}? Se borran su tarifa y sus ajustes.`,
        confirmLabel: 'Quitar',
        onConfirm: async () => {
          setConfirm(null);
          if (await quitarHerramienta(p.contrato.id, t.app_id)) {
            ok(`"${t.name}" quitada del contrato`);
            recargar();
          }
        }
      })
    }
  ];

  return (
    <div className="module-proceso">
      <div className="module-header">
        <h1>Costos Proveedor</h1>
        <p>Tarifa de cada herramienta según el contrato con su proveedor</p>
      </div>

      <div className="consumo-toolbar">
        <span className="contrato-vigente-label">Vigente en</span>
        <div className="consumo-nav">
          <button type="button" className="consumo-nav-btn" onClick={() => setPeriodo(sumarMeses(periodo, -1))} aria-label="Mes anterior">‹</button>
          <input
            className="consumo-nav-select contrato-mes"
            type="month"
            value={periodo}
            onChange={(e) => e.target.value && setPeriodo(e.target.value)}
            aria-label="Mes"
          />
          <button type="button" className="consumo-nav-btn" onClick={() => setPeriodo(sumarMeses(periodo, 1))} aria-label="Mes siguiente">›</button>
        </div>
        {periodo !== periodoDesde(0) && (
          <button type="button" className="contrato-link-btn" onClick={() => setPeriodo(periodoDesde(0))}>
            Ir al mes actual
          </button>
        )}
      </div>

      {loading && !datos ? (
        <p className="proceso-hint">Cargando…</p>
      ) : (
        <div className="prov-tablas">
          {PROVEEDORES.map(({ id, label }) => {
            const p = porProveedor(id);
            if (!p) return <section key={id} className={`prov-tabla prov-borde-${id}`} />;
            const c = p.contrato;
            const sinTarifa = c ? p.catalogo.filter((h) => !p.tarifas.some((t) => t.app_id === h.id)) : [];
            const estado = c && estadoContrato(c);
            return (
              <section key={id} className={`prov-tabla prov-borde-${id}`} aria-label={label}>
                <header className="prov-tabla-head">
                  <span className={`prov-swatch prov-${id}`} />
                  <span className="prov-tabla-titulo">{label}</span>
                  {c && <span className="consumo-cuenta">{p.tarifas.length}</span>}
                  {c && (
                    <span className="prov-tabla-menu">
                      <MenuAcciones label={`Acciones del contrato de ${label}`} acciones={accionesContrato(p)} />
                    </span>
                  )}
                </header>

                {!c ? (
                  <div className="contrato-vacio">
                    <p>Sin contrato vigente en {nombreMes(periodo)}</p>
                    <button
                      type="button"
                      className="btn-primary consumo-cargar-btn"
                      onClick={() => setContratoModal({ modo: 'nuevo', proveedor: id, herramientas: herramientasPara(p) })}
                    >
                      Registrar contrato
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="contrato-info">
                      <div>
                        <span className="contrato-ref">{c.referencia || 'Contrato'}</span>
                        <span className="contrato-fechas-txt">
                          {fechaCorta(c.fecha_inicio)} → {fechaCorta(c.fecha_fin)}
                        </span>
                      </div>
                      <span className={`contrato-estado ${estado.clase}`}>{estado.texto}</span>
                      {c.notas && <p className="consumo-ayuda contrato-notas-txt">{c.notas}</p>}
                    </div>
                    <table className="consumo-tabla">
                      <thead>
                        <tr>
                          <th>Herramienta</th>
                          <th className="num">Tarifa (COP)</th>
                          <th aria-label="Acciones" />
                        </tr>
                      </thead>
                      <tbody>
                        {p.tarifas.length === 0 && (
                          <tr><td colSpan={3} className="consumo-tabla-vacia">Sin herramientas en el contrato</td></tr>
                        )}
                        {p.tarifas.map((t) => (
                          <tr key={t.app_id}>
                            <td>
                              {t.name}
                              {t.ajustada && (
                                <span
                                  className="contrato-ajustada"
                                  title={`Ajustada desde ${fechaCorta(t.vigente_desde)}. Ver historial en ⋮`}
                                >
                                  Ajustada
                                </span>
                              )}
                            </td>
                            <td className="num">
                              {t.costo === null
                                ? <span className="consumo-ayuda">Rige desde {fechaCorta(t.vigente_desde)}</span>
                                : formatCurrencyInt(t.costo)}
                            </td>
                            <td className="contrato-fila-menu">
                              <MenuAcciones label={`Acciones de ${t.name}`} acciones={accionesTarifa(p, t)} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {sinTarifa.length > 0 && (
                      <div className="contrato-sin-tarifa">
                        <span className="contrato-sin-tarifa-titulo">En el catálogo, sin tarifa en este contrato</span>
                        {sinTarifa.map((h) => (
                          <div key={h.id} className="contrato-sin-tarifa-fila">
                            <span>{h.name}</span>
                            <button
                              type="button"
                              className="contrato-link-btn"
                              onClick={() => setTarifaModal({
                                contrato: c,
                                herramienta: { app_id: h.id, name: h.name, costo: h.costo },
                                agregar: true
                              })}
                            >
                              Agregar
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </section>
            );
          })}
        </div>
      )}

      {contratoModal && (
        <ContratoModal
          {...contratoModal}
          onClose={() => setContratoModal(null)}
          onSubmit={guardarContrato}
        />
      )}

      {tarifaModal && (
        <TarifaModal
          {...tarifaModal}
          periodo={periodo}
          onClose={() => setTarifaModal(null)}
          onSubmit={async (t) => {
            const r = await guardarTarifa(tarifaModal.contrato.id, t);
            if (r) {
              ok(tarifaModal.agregar ? `"${t.name}" agregada al contrato` : `Tarifa de "${t.name}" ajustada desde ${nombreMes(t.desde)}`);
              recargar();
            }
            return r;
          }}
        />
      )}

      {historial && (
        <HistorialModal
          {...historial}
          onClose={() => setHistorial(null)}
          onDeshacer={(h) => setConfirm({
            title: 'Deshacer ajuste',
            message: `¿Deshacer el ajuste a ${formatCurrencyInt(h.costo)} desde ${fechaCorta(h.vigente_desde)}? Vuelve a regir la tarifa anterior.`,
            confirmLabel: 'Deshacer',
            onConfirm: async () => {
              setConfirm(null);
              if (await deshacerAjuste(h.id)) {
                ok('Ajuste deshecho');
                setHistorial(null);
                recargar();
              }
            }
          })}
        />
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          confirmLabel={confirm.confirmLabel}
          tone={confirm.tone}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}
    </div>
  );
};
